"use client";

import { useCallback, useRef, useState } from "react";
import { useEffectivePass } from "../hooks/useEffectivePass";

const COUNT_KEY = "jcg_solution_view_count_v1";
const VIEWED_KEY = "jcg_viewed_solution_ids_v1";
const DEFAULT_FREQUENCY = 3;
const VIEWED_MAX_SIZE = 500;

interface UseSolutionRevealAdOptions {
  frequency?: number;
  /**
   * 광고 모달을 실제로 렌더할 슬롯이 있는가.
   * 이 훅의 Promise 는 모달의 onClose → closeAd() 로만 resolve 되므로,
   * 렌더할 슬롯이 없는 앱(JBGP 처럼 광고 상품이 없는 경우)에서 true 로 두면
   * 호출부의 await 가 영원히 풀리지 않아 해설이 열리지 않는다.
   * 기본값은 false — 슬롯을 주입한 앱만 켠다.
   */
  hasAdSlot?: boolean;
}

interface UseSolutionRevealAdReturn {
  /** 솔루션을 펼치기 전에 호출. 광고를 띄울 차례면 모달이 노출되고, 닫힐 때까지 Promise가 보류된다. 호출 후에 기존 펼침 로직을 이어서 진행. */
  requestReveal: (solutionId: string | undefined | null) => Promise<void>;
  /** 광고 모달 노출 상태 */
  isAdOpen: boolean;
  /** 모달 닫기 (보류 중인 Promise resolve) */
  closeAd: () => void;
}

function readCount(): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(COUNT_KEY);
    if (!raw) return 0;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeCount(value: number): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(COUNT_KEY, String(value));
  } catch {}
}

function readViewedIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(VIEWED_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function writeViewedIds(ids: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VIEWED_KEY, JSON.stringify(ids));
  } catch {}
}

/**
 * 풀이 토글 액션 hook.
 *
 * - 본 솔루션 ID Set을 localStorage에 캐시하여, 같은 풀이 재확인은 카운트·광고 모두 스킵
 * - 새 풀이를 펼칠 때마다 카운트 +1, frequency(기본 3)에 도달하면 광고 모달 노출
 * - 광고가 닫힐 때까지 requestReveal Promise가 보류되므로, 호출 코드는 `await` 후 기존 펼침 로직을 이어가면 됨
 * - 단, 모달을 렌더할 슬롯이 없으면(`hasAdSlot: false`) 보류하지 않는다
 * - 프리미엄/이용권(`isAdFree`) 사용자는 카운트·광고 모두 스킵
 */
export function useSolutionRevealAd(
  options: UseSolutionRevealAdOptions = {},
): UseSolutionRevealAdReturn {
  const { frequency = DEFAULT_FREQUENCY, hasAdSlot = false } = options;
  const { isAdFree, isLoaded } = useEffectivePass();
  const [isAdOpen, setIsAdOpen] = useState(false);
  const pendingResolveRef = useRef<(() => void) | null>(null);

  const requestReveal = useCallback(
    async (solutionId: string | undefined | null): Promise<void> => {
      if (!isLoaded || isAdFree) return;
      if (!solutionId) return;
      if (typeof window === "undefined") return;

      const viewedIds = readViewedIds();
      const viewedSet = new Set(viewedIds);
      if (viewedSet.has(solutionId)) {
        return;
      }

      viewedSet.add(solutionId);
      const nextViewed = Array.from(viewedSet);
      const trimmed =
        nextViewed.length > VIEWED_MAX_SIZE
          ? nextViewed.slice(nextViewed.length - VIEWED_MAX_SIZE)
          : nextViewed;
      writeViewedIds(trimmed);

      const nextCount = readCount() + 1;
      if (nextCount >= frequency) {
        writeCount(0);
        // 띄울 모달이 없으면 보류하지 않고 그대로 통과시킨다. 여기서 Promise 를 만들면
        // resolve 할 주체(모달의 onClose)가 없어 해설이 영영 안 열린다.
        if (!hasAdSlot) return;
        return new Promise<void>((resolve) => {
          pendingResolveRef.current = resolve;
          setIsAdOpen(true);
        });
      }
      writeCount(nextCount);
    },
    [isAdFree, isLoaded, frequency, hasAdSlot],
  );

  const closeAd = useCallback(() => {
    setIsAdOpen(false);
    const resolve = pendingResolveRef.current;
    pendingResolveRef.current = null;
    resolve?.();
  }, []);

  return { requestReveal, isAdOpen, closeAd };
}
